// A single assistant-drawer message. Lean by design: the platform assistant
// only ever renders text, multiple-choice clarifications, and action-proposal
// cards — never charts, tables, or SQL (plan 2026-07-28 §5).
import { useState } from "react";
import { Alert, Box, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconAlertCircle, IconCheck, IconRefresh } from "@tabler/icons-react";

import MarkdownContent from "@/components/chat/message/MarkdownContent";
import StreamErrorNotice from "@/components/chat/message/StreamErrorNotice";
import { ActionProposalCard } from "@/components/chat/actionProposals/ActionProposalCard";
// Side-effect: registers the built-in action executors (e.g. create_segment)
// so the cards below can execute on confirm.
import "@/components/chat/actionProposals/builtinActions";
import type { MessageType } from "@/pages/ChatPage";

interface Props {
  msg: MessageType;
  /** Send the chosen MC option as the next turn. */
  onSelectOption: (messageId: string, option: string) => void;
  /** Re-send the last user message (error recovery). */
  onRetry: () => void;
  /** True while a reply is streaming — disables MC + retry interactions. */
  busy: boolean;
}

export function AssistantMessage({ msg, onSelectOption, onRetry, busy }: Props) {
  const persisted = msg.multipleChoice?.selected_option ?? null;
  const [selectedOption, setSelectedOption] = useState<string | null>(persisted);

  if (msg.sender === "user") {
    return (
      <Group justify="flex-end">
        <Paper
          bg="var(--mantine-color-blue-6)"
          c="white"
          px="md"
          py="xs"
          radius="lg"
          maw="85%"
        >
          <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {msg.content}
          </Text>
        </Paper>
      </Group>
    );
  }

  const isError = msg.responseType === "error";

  return (
    <Box maw="95%">
      <Stack gap="xs">
        {isError ? (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title="Something went wrong"
          >
            <Stack gap="xs">
              <Text size="sm">{msg.content}</Text>
              {msg.error?.recoverable !== false && (
                <Group>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconRefresh size={14} />}
                    onClick={onRetry}
                    disabled={busy}
                  >
                    Try again
                  </Button>
                </Group>
              )}
            </Stack>
          </Alert>
        ) : (
          msg.content && (
            <Box fz="sm">
              <MarkdownContent content={msg.content} />
            </Box>
          )
        )}

        {msg.multipleChoice && msg.multipleChoice.options.length > 0 && (
          <Stack gap="xs" mt={4}>
            {msg.multipleChoice.options.map((option, idx) => {
              const isSelected = selectedOption === option;
              const isDisabled = busy || (selectedOption !== null && !isSelected);
              return (
                <Button
                  key={idx}
                  variant={isSelected ? "light" : "outline"}
                  color={isSelected ? "teal" : undefined}
                  size="sm"
                  radius="md"
                  fullWidth
                  justify="start"
                  disabled={isDisabled}
                  leftSection={isSelected ? <IconCheck size={16} /> : undefined}
                  onClick={() => {
                    if (selectedOption || busy) return;
                    setSelectedOption(option);
                    onSelectOption(msg.id, option);
                  }}
                  styles={{
                    root: { fontWeight: isSelected ? 500 : 400, height: "auto", whiteSpace: "normal", padding: "10px 14px" },
                    label: { whiteSpace: "normal", lineHeight: 1.4 },
                  }}
                >
                  {option}
                </Button>
              );
            })}
          </Stack>
        )}

        {msg.actionProposals?.map((proposal) => (
          <ActionProposalCard key={proposal.proposal_id} messageId={msg.id} proposal={proposal} />
        ))}

        {/* Error that arrived after this answer had already committed text —
            the answer stays, the error is a notice underneath. */}
        {msg.streamError && (
          <StreamErrorNotice
            code={msg.streamError.code}
            message={msg.streamError.message}
            recoverable={msg.streamError.recoverable}
            onRetry={busy ? undefined : onRetry}
          />
        )}
      </Stack>
    </Box>
  );
}
